import React from 'react';
import { PropInfo, CodeExample, PhoneProps } from '@cimpress/react-components';

import PhoneCode from '!raw-loader!./phone.jsx';
import PhoneDemo from './phone.jsx';
import ComponentDoc from '../../shared/TabbedComponentDoc';

const PhoneDocs = () => {
  const propInfos: PropInfo<PhoneProps>[] = [
    {
      name: 'initialValue',
      type: 'string',
      default: '',
      description: 'If included, the phone component will parse and prepopulate with the initial value on mount.',
    },
    {
      name: 'onChange',
      type: 'function (required)',
      default: '',
      description: (
        <div>
          <p>Callback function that is fired when any part of the phone component changes. </p>
          <p>
            Signature: <code>function(&#123; number: string, isValid: boolean &#125;) {'=>'} void</code>{' '}
          </p>
          <p>
            <code>number:</code> E.123 formatted telephone number as you type.
          </p>
          <p>
            <code>isValid:</code> Is the string a valid E.123 phone number.
            <br />
            <br />
            <strong>Note:</strong> Properly formatted numbers that do not exist will be considered invalid.{' '}
          </p>
        </div>
      ),
    },
    {
      name: 'triggerChangeOnMount',
      type: 'boolean',
      default: 'false',
      description: 'Treats mounting the component as a change event. Useful for when passing an initial value.',
    },
    {
      name: 'disableStatus',
      type: 'boolean',
      default: 'false',
      description: 'Set to true to prevent validation styling on text inputs when phone number is invalid.',
    },
    {
      name: 'className',
      type: 'string',
      default: '',
      description: 'Additional CSS classes to apply to the container element.',
    },
    {
      name: 'countrySelectLabel',
      type: 'node',
      default: 'Country Code...',
      description: 'Overrides the label for the country code select dropdown.',
    },
    {
      name: 'countrySelectClassName',
      type: 'string',
      default: '',
      description: 'Additional CSS classes to apply to the country code select dropdown.',
    },
    {
      name: 'countrySelectMenuStyle',
      type: 'object',
      default: '',
      description: 'Styles to be applied to the country select drop down menu.',
    },
    {
      name: 'phoneTextInputLabel',
      type: 'node',
      default: 'Telephone (Allowed characters: 0-9)',
      description: 'Overrides the label for the phone number text input.',
    },
    {
      name: 'phoneTextInputClassName',
      type: 'string',
      default: '',
      description: 'Additional CSS classes to apply to the phone number text input.',
    },
    {
      name: 'extensionTextInputLabel',
      type: 'node',
      default: 'Ext.',
      description: 'Overrides the label for the phone number text input.',
    },
    {
      name: 'extensionTextInputClassName',
      type: 'string',
      default: '',
      description: 'Additional CSS classes to apply to the extension text input.',
    },
    {
      name: 'selectedSelect',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Selector component to use for the country selection (i.e. react-select-plus).</p>
          <p>If not specified - react-select will be used.</p>
        </div>
      ),
    },
    {
      name: 'countryGroups',
      type: 'object',
      default: '',
      description: (
        <div>
          <p>Optional set of country groups.</p>
          <p>
            Every country group is represented by a member that has a name (that will be used as a label for this group)
            and a list of country codes to include (or * for all countries)
          </p>
          <p>Example:</p>
          <p>
            <code>&#123; 'Frequent': ['us', 'gb'], 'All': '*' &#125;</code>
          </p>
        </div>
      ),
    },
  ];

  return (
    <div>
      <ComponentDoc name="Phone" propInfos={propInfos}>
        <PhoneDemo />
        <CodeExample code={PhoneCode} />
      </ComponentDoc>
    </div>
  );
};

export default PhoneDocs;
